import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Menu,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBell,
  IconBellRinging,
  IconBuilding,
  IconFileInvoice,
  IconShield,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { NotificationOutput } from '@teo/backend';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { useNotificationConnection } from '../hooks/useNotification';
import { trpc } from '../lib/trpc';

export function NotificationBell() {
  const [opened, { open, close }] = useDisclosure(false);
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { isConnected, connectionStatus } = useNotificationConnection();

  const { data: notificationsData, isLoading: isLoadingNotifications } = useQuery({
    ...trpc.notifications.getNotifications.queryOptions({
      limit: 100,
      offset: 0,
      statusFilter: 'unread',
    }),
    enabled: !authLoading && !!user,
  });

  const markAsReadMutation = useMutation({
    ...trpc.notifications.markAsRead.mutationOptions(),
  });

  const handleMarkAsRead = (notificationId: string) => {
    markAsReadMutation.mutate({ notificationIds: [notificationId] });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'invite_accepted':
        return <IconUser size="1rem" />;
      case 'property_added':
        return <IconBuilding size="1rem" />;
      case 'permission_granted':
        return <IconShield size="1rem" />;
      case 'invoice_auto_matched':
      case 'invoice_validation_flags':
        return <IconFileInvoice size="1rem" />;
      default:
        return <IconBell size="1rem" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'invite_accepted':
        return 'green';
      case 'property_added':
        return 'orange';
      case 'permission_granted':
        return 'purple';
      case 'invoice_auto_matched':
        return 'teal';
      case 'invoice_validation_flags':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const unreadNotifications = (notificationsData?.notifications || []).slice(0, 5);
  const totalCount = notificationsData?.total || 0;
  const hasUnread = totalCount > 0;
  const hasMoreThanFive = totalCount > 5;

  if (authLoading || !user) {
    return null;
  }

  return (
    <Menu opened={opened} onClose={close} position="bottom-end" shadow="md" width={400} offset={5}>
      <Menu.Target>
        <Tooltip label={`Notifications ${isConnected ? '(Live)' : '(Disconnected)'}`}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={opened ? close : open}
            style={{ position: 'relative' }}
          >
            {hasUnread ? <IconBellRinging size="1.2rem" /> : <IconBell size="1.2rem" />}
            {hasUnread && (
              <Badge
                size="sm"
                color="red"
                variant="filled"
                style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  minWidth: 18,
                  height: 18,
                  fontSize: '0.7rem',
                  padding: '0 4px',
                }}
              >
                {hasMoreThanFive ? '5+' : totalCount}
              </Badge>
            )}
            {/* Connection status indicator */}
            <div
              style={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: isConnected
                  ? '#40c057'
                  : connectionStatus === 'connecting'
                    ? '#ffd43b'
                    : '#fa5252',
                border: '1px solid white',
                zIndex: 1,
              }}
              title={`WebSocket: ${connectionStatus}`}
            />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Paper p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
          <Group justify="space-between" align="center">
            <Text size="sm" fw={600}>
              Notifications
            </Text>
          </Group>
        </Paper>

        <ScrollArea.Autosize mah={400}>
          {isLoadingNotifications ? (
            <Group justify="center" p="md">
              <Loader size="sm" />
            </Group>
          ) : totalCount === 0 ? (
            <Group justify="center" p="md">
              <Text size="sm" c="dimmed">
                No notifications
              </Text>
            </Group>
          ) : (
            <Stack gap="xs" p="xs">
              {unreadNotifications.map((notification: NotificationOutput) => (
                <Paper
                  key={notification.notificationId}
                  p="sm"
                  style={{
                    backgroundColor: notification.readAt
                      ? 'var(--mantine-color-gray-0)'
                      : 'var(--mantine-color-blue-0)',
                    border: notification.readAt
                      ? '1px solid var(--mantine-color-gray-2)'
                      : '1px solid var(--mantine-color-blue-2)',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleMarkAsRead(notification.notificationId)}
                >
                  <Group gap="sm" align="flex-start">
                    <ThemeIcon
                      size="sm"
                      variant="light"
                      color={getNotificationColor(notification.notificationType)}
                    >
                      {getNotificationIcon(notification.notificationType)}
                    </ThemeIcon>

                    <Stack gap="xs" style={{ flex: 1 }}>
                      <Group justify="space-between" align="flex-start">
                        <Text size="sm" fw={notification.readAt ? 400 : 600}>
                          {notification.title}
                        </Text>
                        {!notification.readAt && (
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="blue"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification.notificationId);
                            }}
                          >
                            <IconX size="0.7rem" />
                          </ActionIcon>
                        )}
                      </Group>

                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {notification.message}
                      </Text>

                      <Group justify="space-between" align="center">
                        <Text size="xs" c="dimmed">
                          From: {notification.from}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                          })}
                        </Text>
                      </Group>
                    </Stack>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </ScrollArea.Autosize>
        <>
          <Divider />
          <Group justify="center" p="sm">
            <Button
              size="xs"
              variant="subtle"
              onClick={() => {
                void navigate('/notifications');
                close();
              }}
            >
              {hasMoreThanFive ? `View all ${totalCount} notifications` : 'View all notifications'}
            </Button>
          </Group>
        </>
      </Menu.Dropdown>
    </Menu>
  );
}
